// Archies shared predictive-search UX for the -ext search shells.
//
// Serves BOTH the inline mobile burger-drawer search (predictive-search--drawer) AND the
// desktop / mobile-header search modal (predictive-search--modal). Responsibilities:
//   1. Move each shell's shared "Popular searches" pills (search-suggestions-ext) into that
//      component's results content-wrapper so they show as the focus/empty state, and hide them
//      once a query is typed.
//   2. Pills fill their own component's field and run predictive search.
//   3. For the INLINE drawer only (no dialog wrapper), add the missing close affordances — the X
//      button, click-outside, and Escape. The modal is a dialog-component that closes natively, so
//      those are skipped there.

if (!window.__archiesSearchExtWired) {
  window.__archiesSearchExtWired = true;

  // ---- Overlay coordination + robustness (morph-, cart-mutation-, and bfcache-resilient) ---------------
  //
  // Three invariants, all enforced at the DOCUMENT/body level so they SURVIVE the Section-Rendering morphs
  // that a cart mutation (add / remove a line) runs — which REPLACE the header's drawer + search-modal nodes.
  // The previous guard bound listeners to the #search-modal / header-drawer NODES captured ONCE at load, so
  // after a remove-from-cart morph those nodes were detached and the guard silently died — the recurring
  // "doubled search on burger-open after add→remove→close-cart" bug. Everything below observes document.body
  // (never replaced by a morph) and queries overlays fresh each fire, so node identity no longer matters.
  //
  //   (1) The menu drawer never coexists with the search modal OR the cart drawer (removing the last cart
  //       line left the cart drawer open beside the menu — a big part of the "doubled"/broken screenshots).
  //   (2) On drawer open, re-tuck the search-suggestions pills into the collapsed content-wrapper and collapse
  //       the inline search. THE root cause of the reported bug: the cart-mutation morph re-created the pills
  //       in their loose (visible) position, so the drawer opened showing a stray "Popular searches" panel
  //       over the menu. setupPills() only ran at load, so nothing recovered the placement after the morph.
  //   (3) On a bfcache restore, ALL overlays are forced closed and BOTH real scroll-locks are hard-reset.
  //
  // Desktop-safe (the drawer never opens ≥750px, so onDrawerOpened never fires there) and /search-safe.

  const closeMenuDrawer = () => {
    document.querySelectorAll('header-drawer details.menu-open, header-drawer details[open]').forEach((d) => {
      d.classList.remove('menu-open');
      d.removeAttribute('open');
      d.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  };
  const closeSearchModal = () => {
    const m = document.getElementById('search-modal');
    if (m && typeof m.closeDialog === 'function') { try { m.closeDialog(); } catch (e) {} }
  };
  const closeCartDrawer = () => {
    document.querySelectorAll('cart-drawer-component').forEach((c) => {
      if (typeof c.closeDialog === 'function') { try { c.closeDialog(); } catch (e) {} }
    });
  };
  // Fired the instant the drawer's <details> gains .menu-open: enforce (1)-(3).
  const onDrawerOpened = () => {
    closeSearchModal();          // (1) the search modal must never coexist with the drawer
    closeCartDrawer();           // (1) neither may the cart drawer — removing the last cart line leaves it
                                 //     open/coexisting, which is what the "doubled" screenshots actually showed.
    setupPills();                // (2) THE fix for the reported bug: the cart mutation morphs the header and
                                 //     re-creates the search-suggestions pills in their LOOSE position (visible,
                                 //     outside the collapsed content-wrapper) = the stray "Popular searches"
                                 //     panel. setupPills() re-tucks them into the content-wrapper (hidden while
                                 //     collapsed). It ran only once at load, so it never recovered after a morph.
    collapse(getDrawerComp());   // (3) always open collapsed — belt-and-braces on the expanded popover
  };
  // ONE observer on document.body drives both directions. attributeFilter keeps it cheap; the callback only
  // acts when an overlay TRANSITIONS to open (whichever just opened → close the other), so there's no loop:
  // closing an overlay flips it to the not-open state, which the guards below ignore.
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      const t = m.target;
      if (!t || typeof t.matches !== 'function') continue;
      // menu drawer just opened
      if (t.matches('header-drawer details') && t.classList.contains('menu-open')) onDrawerOpened();
      // search modal (its inner native <dialog>) just opened → close the drawer
      else if (t.matches('dialog') && t.hasAttribute('open') && t.closest('#search-modal')) closeMenuDrawer();
    }
  }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class', 'open'] });

  // ---- (3) bfcache resilience: reset EVERY overlay to CLOSED on a back/forward-cache restore. ------------
  //      Mobile Safari restores "back" as a frozen snapshot WITHOUT re-running load init (DOMContentLoaded
  //      doesn't fire), and showModal() dialogs lose the browser top layer across the restore. If an overlay
  //      was open when the user left — classically the CART DRAWER (open on Add-to-cart → Checkout → Back) —
  //      it's restored stale. This listener persists across the restore, firing on every bfcache back-nav.
  const resetOverlaysHard = () => {
    // Close every DialogComponent — including the SUBCLASSES registered under their OWN tags
    // (<cart-drawer-component>, <quick-add-dialog>, <account-drawer>). Matching by 'dialog-component' alone
    // MISSED them, so their showDialog() `body{position:fixed}` scroll-lock leaked through bfcache = a frozen,
    // unscrollable page — the exact ATC→checkout→Back case this handler exists for. closeDialog() resets that
    // body style; the native dialog[open].close() loop below only clears html[scroll-lock].
    document
      .querySelectorAll('dialog-component, cart-drawer-component, quick-add-dialog, account-drawer')
      .forEach((d) => { if (typeof d.closeDialog === 'function') { try { d.closeDialog(); } catch (e) {} } });
    document.querySelectorAll('dialog[open]').forEach((d) => { try { d.close(); } catch (e) {} });
    closeMenuDrawer();
    // Class-toggle overlay that is neither a <dialog> nor a dialog-component, so the loops above never reach
    // it: the PDP cstm-modal (dark full-screen block). Force it hidden so it can't be restored covering the page.
    document.querySelectorAll('.custom-modal-block:not(.hide-thisx)').forEach((el) => el.classList.add('hide-thisx'));
    // Hard-reset BOTH real scroll-locks directly, so we don't depend on each component's closeDialog succeeding
    // (dialog.js showDialog() sets body{position:fixed;top;width}; the <dialog scroll-lock> toggle sets
    // html[scroll-lock]). The old handler cleared body CLASSES that nothing in this theme ever sets — a no-op.
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.documentElement.removeAttribute('scroll-lock');
  };
  window.addEventListener('pageshow', (event) => { if (event.persisted) resetOverlaysHard(); });

  const isExpanded = (comp) => comp && comp.classList.contains('predictive-search--expanded');

  // Collapse the inline drawer search: hide the results overlay, clear the query, drop focus.
  const collapse = (comp) => {
    if (!comp) return;
    comp.classList.remove('predictive-search--expanded');
    const input = comp.querySelector('input[type="search"]');
    if (input) {
      if (input.value) {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      input.blur();
    }
  };

  // Show the pills INSIDE the results content-wrapper (above the results, which predictive-search.js
  // repopulates) so they appear as the empty/focus state, and hide them once a query is typed.
  const setupPills = () => {
    document.querySelectorAll('predictive-search-component.predictive-search--ext').forEach((comp) => {
      const shell = comp.parentElement;
      if (!shell) return;
      const pills = shell.querySelector('.search-suggestions-ext');
      const wrapper = comp.querySelector('.predictive-search-form__content-wrapper');
      const input = comp.querySelector('input[type="search"]');
      if (!pills || !wrapper) return;
      if (pills.parentElement !== wrapper) {
        wrapper.insertBefore(pills, wrapper.firstChild);
      }
      if (input && !input.__archiesPillSync) {
        input.__archiesPillSync = true;
        const sync = () => {
          pills.hidden = input.value.trim().length > 0;
        };
        input.addEventListener('input', sync);
        sync();
      }
      // Drawer only: mark the component "expanded" while the field is focused. The close (X) button,
      // outside-click and Escape affordances are all gated on `--expanded`, but nothing was adding it —
      // so the popover had no way to be dismissed. Focus adds it; collapse() removes it.
      if (comp.classList.contains('predictive-search--drawer') && input && !input.__archiesExpandSync) {
        input.__archiesExpandSync = true;
        input.addEventListener('focus', () => comp.classList.add('predictive-search--expanded'));
      }
    });
  };

  // Pills → fill the field of the pill's OWN component (drawer or modal) + run predictive search.
  document.addEventListener('click', (event) => {
    const pill = event.target.closest('.search-suggestions-ext__pill[data-search-term]');
    if (!pill) return;
    event.preventDefault();
    const comp = pill.closest('predictive-search-component');
    const input = comp && comp.querySelector('input[type="search"]');
    if (!input) return;
    input.value = pill.getAttribute('data-search-term');
    input.focus();
    // Must be an InputEvent with an inputType — predictive-search.js's search handler bails on
    // `if (!event.inputType) return`, so a plain Event('input') fills the field but never searches.
    input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText' }));
    // [Archies] The pill already chose the query, so drop focus to hide the soft keyboard and reveal
    // the full results — same UX for BOTH mobile shells (drawer + header modal). The drawer also needs
    // --expanded re-asserted so its results overlay (+ close X) stays open; blur alone doesn't collapse it.
    if (comp.classList.contains('predictive-search--drawer')) {
      comp.classList.add('predictive-search--expanded');
    }
    input.blur();
    // The component re-focuses the field as it renders results (async), which would re-open the
    // keyboard — blur again after that settles so the keyboard stays down.
    setTimeout(function () { input.blur(); }, 400);
  });

  // ---- Drawer-only close affordances (inline component, no dialog) ----
  const getDrawerComp = () => document.querySelector('predictive-search-component.predictive-search--drawer');

  document.addEventListener('click', (event) => {
    const comp = getDrawerComp();
    if (!isExpanded(comp)) return;

    // Explicit close (X) button.
    if (event.target.closest('.predictive-search--drawer .predictive-search__close-modal-button')) {
      event.preventDefault();
      collapse(comp);
      return;
    }

    // "Close" text in the popover heading row (search-suggestions-ext show_close).
    if (event.target.closest('.predictive-search--drawer [data-search-close]')) {
      event.preventDefault();
      collapse(comp);
      return;
    }

    // Click anywhere outside the drawer search component → collapse (the outside element's own click
    // still proceeds — we don't preventDefault, so tapping a menu tile both closes search + follows it).
    if (!event.target.closest('.predictive-search--drawer')) {
      collapse(comp);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const comp = getDrawerComp();
    if (isExpanded(comp)) collapse(comp);
  });

  // iOS: while the search is expanded (soft keyboard up), the OS drags the page behind to keep the
  // focused input in view — the page scrolls behind the popover even with overflow:hidden on the menu.
  // Block touchmove everywhere WHILE expanded, except inside the results scroller (which must still
  // scroll). passive:false is required so preventDefault() takes effect on touchmove.
  document.addEventListener('touchmove', (event) => {
    const comp = getDrawerComp();
    if (!isExpanded(comp)) return;
    if (event.target && event.target.closest && event.target.closest('.predictive-search--drawer .predictive-search-form__content')) return;
    event.preventDefault();
  }, { passive: false });

  setupPills();
  document.addEventListener('DOMContentLoaded', setupPills);

  // ---- Okendo re-hydration for async predictive-search results ----
  // Each row renders Okendo's live star rating (<div data-oke-star-rating>), which arrives empty
  // because predictive search injects results via morph. Re-init Okendo after each results change
  // when any star-rating is still un-hydrated. Debounced so fast typing doesn't thrash it.
  // (The "View all" button is Horizon's native one — `.predictive-search-form__footer`; we only
  // style it in predictive-search-ext.css, no custom button needed.)
  const rehydrateOkendo = () => {
    const api = window.okeWidgetApi;
    if (!api || typeof api.initAllWidgets !== 'function') return;
    let pending = false;
    document.querySelectorAll('.predictive-search-form__content [data-oke-star-rating]').forEach((el) => {
      if (!el.querySelector('.oke-sr')) pending = true;
    });
    if (pending) api.initAllWidgets();
  };

  let okendoTimer = null;
  const observeResults = () => {
    document.querySelectorAll('.predictive-search-form__content').forEach((container) => {
      if (container.__archiesResultsObserved) return;
      container.__archiesResultsObserved = true;
      new MutationObserver(() => {
        clearTimeout(okendoTimer);
        okendoTimer = setTimeout(rehydrateOkendo, 300);
      }).observe(container, { childList: true, subtree: true });
    });
  };
  observeResults();
  document.addEventListener('DOMContentLoaded', observeResults);

  // Close the search popover when a result row is clicked. Horizon soft-navigates, and #handleModalClick
  // doesn't close on result clicks, so the modal / inline drawer search would otherwise stay open on
  // the PDP. Close the dialog (header/mobile modal) or collapse the inline drawer search.
  document.addEventListener('click', (event) => {
    const row = event.target.closest('.ps-row-ext');
    if (!row) return;
    const dialog = row.closest('dialog-component');
    if (dialog && typeof dialog.closeDialog === 'function') {
      dialog.closeDialog();
      return;
    }
    const drawerComp = row.closest('.predictive-search--drawer');
    if (drawerComp) collapse(drawerComp);
  });
}
